import { Injectable } from '@angular/core';
import { EventService } from './event.service';
import { environment } from '../../../src/environments/environment';
import { select, Store } from '@ngrx/store';
import { ItemStatus } from 'globals/store/share.data';
import { ShareService } from './share.service';
import { Asset } from 'globals/store/assets.data';
import { UserService } from './user.service';
import { EntitiesService } from './entities.service';
import { nameJoiner } from '../helpers/helpers';
import { User } from 'globals/store/user.data';
import { Ranking } from 'globals/store/events.data';
import { getOneStorePromise } from '../store/ui.era';
import { userFeature } from '../store/user.era';
import { assetsActions } from '../store/assets.era';

@Injectable({ providedIn: 'root' })
export class AssetsService  {

  readonly _file: string = 'assets.service.ts';
  readonly api = `${environment.functionsURL}api/app`;
  
  constructor(
    private eventService: EventService, 
    private userService: UserService,
    private shareService: ShareService,
    private entitiesService: EntitiesService,
    public store: Store
   ) { }

   async addAsset({ name, icon }: Partial<Asset>, associatedId: string = null, status: ItemStatus = "Off") {
    const id = await this.shareService.createSlugCode()
    const asset: Asset = { id, name: name || null, icon: icon || null, status, createdBy: this.userService.uid, createdAt: new Date().getTime(), associatedId }
    await this.upsertAsset(asset)
    return asset
   }

   async upsertAsset(asset: Asset, isNew = false) {
    await this.upsertAssets([asset], isNew)
   }

  async changeAssetStatus(assetId: string, status: ItemStatus) { // for use when asset itself is not passed around
    const { assets } = await this.eventService.getCurrentEvent() // yes, twice, but better to have real update logic below
    const asset = assets[assetId]
    asset.status = status
    this.upsertAssets([asset])
  }

  async upsertAssets(modifiedAssets: Asset[], isNew = false) {
    const user = await getOneStorePromise(this.store, select(userFeature.selectUser)) as User
    modifiedAssets = modifiedAssets.map(a => ({...a})) // needed for store update
    modifiedAssets.forEach(asset => {
      if (!isNew) asset.updatedAt = new Date().getTime()
      asset.updatedBy = nameJoiner(user)
    })
    this.store.dispatch(assetsActions.addAssets(modifiedAssets ))
    await this.eventService.saveEventPageEntities('assets', modifiedAssets)
  }

  async updateTypes(assetCategories: Ranking[]) { // to be compatible with entity-types
    assetCategories.forEach((level, i) => level.rank = assetCategories.length - i)
    const event = await this.eventService.getCurrentEvent()
    await this.eventService.updateEvent(event.eventId, { assetCategories })
  }

  async updateEntities(entities: Asset[]) { // to be compatible with entity-types
    await this.upsertAssets(entities)
  }

  async deleteAssets(ids: string[]) {
    this.store.dispatch(assetsActions.deleteAssets( ids ))
    await this.eventService.deleteFromEventPages('assets', ids)
  }

  async templateAssets(assets: Asset[]) {
    const assetTemplates = await this.entitiesService.createEntityTemplates('asset', assets)
    const updatedAssets = assets.map((a, i) => {
      const { dateAdded, dateModified, templateId, versionNumber } = assetTemplates[i]
      const updatedAsset: Asset = { ...a, dateAdded, dateModified, templateId, versionNumber }
      return updatedAsset
    })
    await this.upsertAssets(updatedAssets)
  }

  }
